import React, { useState } from "react";
import "./price.css";
import { IoMdClose } from "react-icons/io";

export default function PricingPlans() {
  const [activeTab, setActiveTab] = useState("monthly"); // monthly | quarterly | halfyear | yearly
  const [selectedPlan, setSelectedPlan] = useState(null);

  // Shared full details (same across durations)
  const fullDetails = {
    resources: [
      { label: "Dedicated Resources", value: "" }, // values updated per plan below
    ],
    setup: [
      { label: "WhatsApp Group Creation - Team Collaboration", value: "✔" },
      { label: "0 Day Report for MOM Growth Comparison", value: "✔" },
      { label: "Google Analytics - Setup and Review", value: "✔" },
      { label: "Google Search Console - Setup and Review", value: "✔" },
      { label: "Google Penalty Check", value: "✔" },
      { label: "Website Indexing Check", value: "✔" },
      { label: "Business Brief Meeting", value: "✔" },
      { label: "Sitemap Submission", value: "✔" },
    ],
    onpage_seo: [],
    offpage_seo: [],
    technical_seo: [],
    reporting: [],
    support: []
  };

  // Build full details per plan (from screenshots)
  const planDetails = {
    lite: {
      resources: [
        { label: "Dedicated Resources", value: "1" },
        { label: "SEO Executive", value: "1" },
        { label: "Asst. SEO Executive", value: "❌" },
        { label: "Shared Resources", value: "1" },
        { label: "SEO Manager", value: "1" },
        { label: "Project Manager", value: "❌" },
        { label: "Content Writer", value: "Client’s side (or pay separately)" },
        { label: "Web Developer", value: "Client’s side (or pay separately)" }
      ],
      setup: fullDetails.setup,
      onpage_seo: [
        "Keyword Research : 500 keywords",
        "Meta Tags Optimization : 25 pages",
        "Content Optimization : 25 pages",
        "Review Content Quality : ✔",
        "Content Recommendation : ✔",
        "Content Publish on Website : ❌",
        "Header Tags Optimization : 25 pages",
        "Image Optimization : 100 pages",
        "SEO Friendly URL Suggestion : ✔",
        "Internal Link Structuring & Optimization : Up to 200 links",
        "Sitemap Creation : ✔",
        "Robots.txt Optimization : ✔",
        "Google Analytics Setup : ✔",
        "Google Webmaster Tools Setup : ✔"
      ],
      offpage_seo: [
        "Competitor Analysis : Basic",
        "Link Building (Backlinks) : 200",
        "Contextual Links : 100",
        "Dofollow : 50%–65%",
        "Nofollow : 50%–35%",
        "Press Release : ❌",
        "Paid Guest Posts : ❌",
        "Email Outreach : ❌",
        "Skyscraper : ❌"
      ],
      technical_seo: [
        "SEO Audit & Recommendations : ✔",
        "Mobile Optimization : Basic",
        "Speed Optimization : Basic",
        "HTTPS Setup : ✔",
        "Canonicalization : ✔",
        "Error 404 Fix : Basic",
        "Redirects : Basic",
        "Schema Markup : Home Page",
        "Page Indexing : ✔"
      ],
      reporting: [
        "Planning : Basic",
        "Strategy : Basic",
        "SEO Reports : Monthly",
        "Keyword Ranking Report : Monthly",
        "Backlinks Report : Monthly",
        "Daily Updates in Group : ❌"
      ],
      support: []
    },

    standard: {
      resources: [
        { label: "Dedicated Resources", value: "2" },
        { label: "SEO Executive", value: "1" },
        { label: "Asst. SEO Executive", value: "1" },
        { label: "Shared Resources", value: "1" },
        { label: "SEO Manager", value: "1" },
        { label: "Project Manager", value: "❌" },
        { label: "Content Writer", value: "Client’s side (or pay separately)" },
        { label: "Web Developer", value: "Wordpress only" }
      ],
      setup: fullDetails.setup,
      onpage_seo: [
        "Keyword Research : 1000 keywords",
        "Meta Tags Optimization : 100 pages",
        "Content Optimization : 100 pages",
        "Review Content Quality : ✔",
        "Content Recommendation : ✔",
        "Content Publish on Website : ✔",
        "Header Tags Optimization : 100 pages",
        "Image Optimization : 100 pages",
        "SEO Friendly URL Suggestion : ✔",
        "Internal Link Structuring & Optimization : Up to 200 links",
        "Sitemap Creation : ✔",
        "Robots.txt Optimization : ✔",
        "Google Analytics Setup : ✔",
        "Google Webmaster Tools Setup : ✔"
      ],
      offpage_seo: [
        "Competitor Analysis : Intermediate",
        "Link Building (Backlinks) : 300",
        "Contextual Links : 200",
        "Dofollow : 60%–70%",
        "Nofollow : 40%–30%",
        "Press Release : ❌",
        "Paid Guest Posts : ❌",
        "Email Outreach : ❌",
        "Skyscraper : ❌"
      ],
      technical_seo: [
        "SEO Audit & Recommendations : ✔",
        "Mobile Optimization : Intermediate",
        "Speed Optimization : Intermediate",
        "HTTPS Setup : ✔",
        "Canonicalization : ✔",
        "Error 404 Fix : Intermediate",
        "Redirects : Intermediate",
        "Schema Markup : 5 Pages",
        "Page Indexing : ✔"
      ],
      reporting: [
        "Planning : Intermediate",
        "Strategy : Intermediate",
        "SEO Reports : Monthly",
        "Keyword Ranking Report : Monthly",
        "Backlinks Report : Monthly",
        "Daily Updates in Group : ❌"
      ],
      support: [
        { label: "Phone Support", value: "✔" },
        { label: "Chat Support", value: "✔" },
        { label: "Email Support", value: "✔" },
        { label: "Monthly Consultation", value: "2 hours" },
        { label: "PRP (Performance Review & Planning)", value: "Monthly" }
      ]
    },

    advance: {
      resources: [
        { label: "Dedicated Resources", value: "2" },
        { label: "SEO Executive", value: "1" },
        { label: "Asst. SEO Executive", value: "1" },
        { label: "Shared Resources", value: "2" },
        { label: "SEO Manager", value: "1" },
        { label: "Project Manager", value: "1" },
        { label: "Content Writer", value: "Client’s side (or pay separately)" },
        { label: "Web Developer", value: "Wordpress only" }
      ],
      setup: fullDetails.setup,
      onpage_seo: [
        "Keyword Research : 1500 keywords",
        "Meta Tags Optimization : 300 pages",
        "Content Optimization : 300 pages",
        "Review Content Quality : ✔",
        "Content Recommendation : ✔",
        "Content Publish on Website : ✔",
        "Header Tags Optimization : 300 pages",
        "Image Optimization : 300 pages",
        "SEO Friendly URL Suggestion : ✔",
        "Internal Link Structuring & Optimization : Up to 600 links",
        "Sitemap Creation : ✔",
        "Robots.txt Optimization : ✔",
        "Google Analytics Setup : ✔",
        "Google Webmaster Tools Setup : ✔"
      ],
      offpage_seo: [
        "Competitor Analysis : Advanced",
        "Link Building (Backlinks) : 500",
        "Contextual Links : 300",
        "Dofollow : 65%–75%",
        "Nofollow : 35%–25%",
        "Press Release : 20 Platforms",
        "Paid Guest Posts : ❌",
        "Email Outreach : ❌",
        "Skyscraper : ❌"
      ],
      technical_seo: [
        "SEO Audit & Recommendations : ✔",
        "Mobile Optimization : Advanced",
        "Speed Optimization : Advanced",
        "HTTPS Setup : ✔",
        "Canonicalization : ✔",
        "Error 404 Fix : Comprehensive",
        "Redirects : Comprehensive",
        "Schema Markup : 10 Pages",
        "Page Indexing : ✔"
      ],
      reporting: [
        "Planning : Advanced",
        "Strategy : Advanced",
        "SEO Reports : Monthly",
        "Keyword Ranking Report : Monthly",
        "Backlinks Report : Bi-Weekly",
        "Daily Updates in Group : ✔"
      ],
      support: [
        { label: "Phone Support", value: "✔" },
        { label: "Chat Support", value: "✔" },
        { label: "Email Support", value: "✔" },
        { label: "Monthly Consultation", value: "4 hours" },
        { label: "PRP (Performance Review & Planning)", value: "Monthly" }
      ]
    },

    enterprise: {
      resources: [
        { label: "Dedicated Resources", value: "2" },
        { label: "SEO Executive", value: "1" },
        { label: "Asst. SEO Executive", value: "1" },
        { label: "Shared Resources", value: "4" },
        { label: "SEO Manager", value: "1" },
        { label: "Project Manager", value: "1" },
        { label: "Content Writer", value: "1" },
        { label: "Web Developer", value: "1" }
      ],
      setup: fullDetails.setup,
      onpage_seo: [
        "Keyword Research : 2500+ keywords",
        "Meta Tags Optimization : Unlimited pages",
        "Content Optimization : All pages",
        "Review Content Quality : ✔",
        "Content Recommendation : ✔",
        "Content Publish on Website : ✔",
        "Header Tags Optimization : All pages",
        "Image Optimization : All images",
        "SEO Friendly URL Suggestion : ✔",
        "Internal Link Structuring & Optimization : Unlimited",
        "Sitemap Creation : ✔",
        "Robots.txt Optimization : ✔",
        "Google Analytics Setup : ✔",
        "Google Webmaster Tools Setup : ✔"
      ],
      offpage_seo: [
        "Competitor Analysis : Advanced",
        "Link Building (Backlinks) : 1000+",
        "Contextual Links : 500+",
        "Dofollow : 70%–80%",
        "Nofollow : 30%–20%",
        "Press Release : Multiple Platforms",
        "Paid Guest Posts : ✔ (custom)",
        "Email Outreach : ✔ (custom)",
        "Skyscraper : ✔ (custom)"
      ],
      technical_seo: [
        "SEO Audit & Recommendations : ✔",
        "Mobile Optimization : Advanced",
        "Speed Optimization : Advanced",
        "HTTPS Setup : ✔",
        "Canonicalization : ✔",
        "Error 404 Fix : Comprehensive",
        "Redirects : Comprehensive",
        "Schema Markup : All Pages",
        "Page Indexing : ✔"
      ],
      reporting: [
        "Planning : Advanced",
        "Strategy : Advanced",
        "SEO Reports : Monthly",
        "Keyword Ranking Report : Monthly",
        "Backlinks Report : Weekly/Bi-Weekly",
        "Daily Updates in Group : ✔"
      ],
      support: [
        { label: "Phone Support", value: "✔" },
        { label: "Chat Support", value: "✔" },
        { label: "Email Support", value: "✔" },
        { label: "Monthly Consultation", value: "8 hours" },
        { label: "PRP (Performance Review & Planning)", value: "Monthly" }
      ]
    }
  };

  // Price table for each duration (from your screenshots / confirmed)
  const priceTable = {
    monthly: {
      lite: "₹20,000",
      standard: "₹35,000",
      advance: "₹50,000",
      enterprise: "₹150,000"
    },
    quarterly: {
      lite: "₹18,000",
      standard: "₹31,500",
      advance: "₹45,000",
      enterprise: "₹135,000"
    },
    halfyear: {
      lite: "₹16,000",
      standard: "₹28,000",
      advance: "₹40,000",
      enterprise: "₹120,000"
    },
    yearly: {
      lite: "₹14,000",
      standard: "₹24,500",
      advance: "₹35,000",
      enterprise: "₹105,000"
    }
  };

  // Build plans array from priceTable for the active tab
  const planMeta = [
    { id: "lite", title: "LITE", type: "Small Business" },
    { id: "standard", title: "STANDARD", type: "Moderate Competition" },
    { id: "advance", title: "ADVANCE", type: "Ecommerce / High Competition" },
    { id: "enterprise", title: "ENTERPRISE", type: "Brands / High Competition" }
  ];

  const plans = planMeta.map(p => ({
    id: p.id,
    title: p.title,
    price: priceTable[activeTab][p.id],
    type: p.type,
    features: (() => {
      if (p.id === "lite") return [
        "Dedicated SEO Executive, Shared SEO Manager",
        "Up to 500 keywords research and Basic On-page & Off-Page",
        "Basic Technical SEO issues solving",
        "Basic Strategy planning and Monthly reporting",
        "1 hour Monthly Consultation and PRP"
      ];
      if (p.id === "standard") return [
        "Dedicated SEO Executive, Assistant, and Shared SEO Manager",
        "Up to 1000 keywords research and Intermediate On-page & Off-Page",
        "Intermediate Technical SEO issues solving",
        "Intermediate Strategy planning and Monthly reporting",
        "2 hours Monthly Consultation and PRP"
      ];
      if (p.id === "advance") return [
        "Dedicated SEO Executive, Assistant, Shared Project & SEO Manager",
        "Up to 1500 keywords research and Advanced On-page & Off-Page",
        "Advanced Technical SEO issues solving",
        "Advanced Strategy planning and Bi-weekly reporting",
        "4 hours Monthly Consultation and PRP"
      ];
      return [
        "Team of SEO Executive, Assistant, Project & SEO Manager, Content...",
        "2500+ keywords research and Comprehensive On-page & Off-Page",
        "All-inclusive Technical SEO issues solving",
        "Comprehensive Strategy planning and Bi-weekly reporting",
        "8 hours Monthly Consultation and PRP"
      ];
    })(),
    details: planDetails[p.id]
  }));

  return (
    <div className="pricing-wrapper">
      <section className="price-header" />
      <section className="services-header">
        <div className="photo-text">SEO
          <p>
            Packages & Plans
          </p>
        </div>
      </section>

      <div className="tabs">
        {[
          { id: "monthly", label: <>Month <br /> (1 Month)</> },
          { id: "quarterly", label: <>Quarterly <br /> (3 Months)</> },
          { id: "halfyear", label: <>Half Yearly <br /> (6 Months)</> },
          { id: "yearly", label: <>Yearly <br /> (12 Months)</> },
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`tab-btn ${activeTab === tab.id ? "active" : ""}`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div className="plans-grid">
        {plans.map(plan => (
          <div key={plan.id} className="plan-card">
            {/* POPULAR TAG */}
            {plan.id === "standard" && <div className="popular-tag">Popular</div>}

            {/* DISCOUNT + PRICE */}
            <h3 className="plan-title">{plan.title}</h3>

            <div className="price-section">
              {/* Show original price & discount only if NOT monthly */}
              {activeTab !== "monthly" && (
                <>
                  <p className="original-price">
                    ₹{(() => {
                      const monthlyPrices = {
                        lite: 20000,
                        standard: 35000,
                        advance: 50000,
                        enterprise: 150000
                      };

                      // Multiplying months so original price = base × months
                      const multipliers = {
                        quarterly: 1,
                        halfyear: 1,
                        yearly: 1
                      };

                      return (monthlyPrices[plan.id] * multipliers[activeTab]).toLocaleString();
                    })()}
                  </p>

                  {/* Dynamic discount badge */}
                  <span className="discount-badge">
                    {activeTab === "quarterly" && "10% Off"}
                    {activeTab === "halfyear" && "20% Off"}
                    {activeTab === "yearly" && "30% Off"}
                  </span>
                </>
              )}
            </div>


            <h2 className="plan-price">{plan.price}</h2>
            <p className="per-month">/Month</p>
            <button className="quote-btn">Get Quote</button>

            <hr className="divider" />
            <p className="plan-type">{plan.type}</p>

            <ul className="feature-list">
              {plan.features.map((f, idx) => (
                <li key={idx}>{f}</li>
              ))}
            </ul>

            <button className="details-btn" onClick={() => setSelectedPlan(plan)}>
              <span className="details-btn-txt">View Details</span>
            </button>
          </div>

        ))}
      </div>

      {selectedPlan && (
        <div className="popup-overlay">
          <div className="popup-box">
            <button className="close-btn" onClick={() => setSelectedPlan(null)}><IoMdClose /></button>
            <h2 className="popup-title">{selectedPlan.title}</h2>

            <div className="popup-grid">
              {/* Resources */}
              <div className="card">
                <h3 className="popup-subtitle">Resources</h3>
                <ul>
                  {selectedPlan.details.resources.map((r, i) => (
                    <li key={i}>
                      <span className="left">{r.label}</span>
                      <span className="sep">:</span>
                      <span className="right">{r.value}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Initial Setup & Analysis */}
              <div className="card">
                <h3 className="popup-subtitle">Initial Setup & Analysis</h3>
                <ul>
                  {selectedPlan.details.setup.map((s, i) => (
                    <li key={i}>
                      <span className="left">{s.label}</span>
                      <span className="sep">:</span>
                      <span className="right">{s.value}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* On-page SEO */}
              <div className="card">
                <h3 className="popup-subtitle">On-page SEO</h3>
                <ul>
                  {selectedPlan.details.onpage_seo.map((s, i) => {
                    const parts = s.split(" : ");
                    return (
                      <li key={i}>
                        <span className="left">{parts[0]}</span>
                        <span className="sep">:</span>
                        <span className="right">{parts[1] || ""}</span>
                      </li>
                    );
                  })}
                </ul>
              </div>

              {/* Off-page SEO */}
              <div className="card">
                <h3 className="popup-subtitle">Off-Page SEO</h3>
                <ul>
                  {selectedPlan.details.offpage_seo.map((s, i) => {
                    const parts = s.split(" : ");
                    return (
                      <li key={i}>
                        <span className="left">{parts[0]}</span>
                        <span className="sep">:</span>
                        <span className="right">{parts[1] || ""}</span>
                      </li>
                    );
                  })}
                </ul>
              </div>

              {/* Technical SEO */}
              <div className="card">
                <h3 className="popup-subtitle">Technical SEO</h3>
                <ul>
                  {selectedPlan.details.technical_seo.map((s, i) => {
                    const parts = s.split(" : ");
                    return (
                      <li key={i}>
                        <span className="left">{parts[0]}</span>
                        <span className="sep">:</span>
                        <span className="right">{parts[1] || ""}</span>
                      </li>
                    );
                  })}
                </ul>
              </div>

              {/* Planning & Reporting */}
              <div className="card">
                <h3 className="popup-subtitle">Planning & Reporting</h3>
                <ul>
                  {selectedPlan.details.reporting.map((s, i) => {
                    const parts = s.split(" : ");
                    return (
                      <li key={i}>
                        <span className="left">{parts[0]}</span>
                        <span className="sep">:</span>
                        <span className="right">{parts[1] || ""}</span>
                      </li>
                    );
                  })}
                </ul>
              </div>

              {/* Support & Consultation (if exists) */}
              {selectedPlan.details.support && selectedPlan.details.support.length > 0 && (
                <div className="card">
                  <h3 className="popup-subtitle">Support & Consultation</h3>
                  <ul>
                    {selectedPlan.details.support.map((s, i) => (
                      <li key={i}>
                        <span className="left">{s.label}</span>
                        <span className="sep">:</span>
                        <span className="right">{s.value}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>


            <a
              href={`https://wa.me/919624883636?text=${encodeURIComponent(
                `Hi! I'm interested in the ${selectedPlan.title} plan (${activeTab.toUpperCase()}). Please share more details.`
              )}`}
              target="_blank"
              rel="noopener noreferrer"
              className="subscribe-btn"
            >
              Subscribe Now
            </a>

          </div>
        </div>
      )}
    </div>
  );
}
